import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { SUPPORTED_LOCALES, normalizeLocale, translate } from "./localeCore";

const STORAGE_KEY = "orca:locale";

const messages = {
  es: {
    "language.es": "ES",
    "language.en": "EN",
    "language.change": "Cambiar idioma",
    "common.loading": "Cargando…",
    "common.retry": "Reintentar",
    "common.close": "Cerrar",
    "production.title": "Producción",
    "production.projects": "Proyectos",
    "production.project": "Proyecto",
    "production.openShots": "Abrir planos",
    "production.search": "Buscar plano, escena o artista",
    "production.refresh": "Actualizar",
    "production.select": "Seleccionar",
    "production.assignee": "Responsable",
    "production.assignSelected": "Asignar selección ({count})",
    "production.bulkResult": "Asignados: {ok} · conflictos/fallos: {failed}",
    "production.open": "Abrir",
    "production.empty": "No hay planos que coincidan con estos filtros.",
    "production.metric.total": "Planos",
    "production.metric.unassigned": "Sin asignar",
    "production.metric.review": "En revisión",
    "production.metric.changes": "Con cambios",
    "production.metric.approved": "Aprobados",
    "production.metric.readyTakes": "Con takes listos",
    "production.filter.all": "Todos",
    "production.filter.mine": "Mis planos",
    "production.filter.unassigned": "Sin asignar",
    "production.filter.in_progress": "En progreso",
    "production.filter.in_review": "En revisión",
    "production.filter.changes_requested": "Cambios",
    "production.filter.approved": "Aprobados",
    "production.column.shot": "Plano",
    "production.column.assignee": "Responsable",
    "production.column.state": "Estado",
    "production.column.latest": "Última",
    "production.column.approved": "Aprobada",
    "production.column.signatures": "Firmas",
    "production.column.takes": "Takes listos",
    "production.column.activity": "Actividad",
    "production.column.actions": "Acciones",
    "production.state.unassigned": "Sin asignar",
    "production.state.todo": "Pendiente",
    "production.state.inProgress": "En progreso",
    "production.state.ready": "Lista para revisión",
    "production.state.inReview": "En revisión",
    "production.state.changes": "Cambios pedidos",
    "production.state.approved": "Aprobada",
    "production.state.rejected": "Rechazada",
    "production.state.skipped": "Omitida",
    "workflow.title": "Producción del plano",
    "workflow.assignee": "Responsable",
    "workflow.unassigned": "Sin asignar",
    "workflow.priority": "Prioridad",
    "workflow.latest": "Última versión",
    "workflow.approved": "Versión aprobada",
    "workflow.noApproved": "Ninguna",
    "workflow.assign": "Asignar",
    "workflow.sending": "Enviando…",
    "workflow.send": "Enviar a aprobación",
    "workflow.sent": "Enviada",
    "workflow.changes": "Cambios pedidos",
    "workflow.versionApproved": "Aprobada",
    "workflow.draft": "Borrador",
    "workflow.policyMissing": "Configura primero la política de aprobación del proyecto.",
    "workflow.signatures": "Firmas",
    "workflow.compatibility": "Modo compatible: las aprobaciones por etapas aún no están activas.",
    "workflow.settings": "Equipo y aprobaciones",
    "workflow.team": "Equipo",
    "workflow.roles": "Funciones",
    "workflow.policy": "Cadena de aprobación",
    "workflow.policy.internal": "VFX + Dirección",
    "workflow.policy.director": "Solo Dirección",
    "workflow.policy.client": "VFX + Dirección + Cliente",
    "workflow.save": "Guardar",
    "workflow.saveChanges": "Guardar cambios",
    "workflow.unsaved": "sin guardar",
    "workflow.saved": "Guardado",
    "workflow.discardConfirm": "Hay funciones marcadas sin guardar. ¿Cerrar y perderlas?",
    "workflow.supervisorDefault": "Quien tiene el rol global Supervisor entra en el proyecto ya como Supervisor VFX. Se le puede quitar aquí.",
    "workflow.invite": "Invitar revisor externo",
    "workflow.name": "Nombre",
    "workflow.email": "Email",
    "workflow.role": "Función",
    "workflow.stage": "Etapa",
    "workflow.createLink": "Crear enlace nominal",
    "workflow.copyLink": "Copiar enlace",
    "workflow.linkCreated": "Enlace creado. El token solo se muestra esta vez.",
    "workflow.unregisteredTakes": "Takes terminados sin versión",
    "workflow.registerTake": "Crear versión",
    "workflow.archiving": "Archivando resultado…",
    "workflow.noVersions": "Este plano todavía no tiene versiones.",
    "review.title": "Revisión de planos",
    "review.as": "Revisando como {name}",
    "review.empty": "No hay versiones enviadas a revisión.",
    "review.stage": "Etapa",
    "review.note": "Nota para el artista",
    "review.approve": "Aprobar",
    "review.requestChanges": "Pedir cambios",
    "review.reject": "Rechazar",
    "review.pending": "Pendiente",
    "review.inReview": "En revisión",
    "review.approved": "Aprobada",
    "review.changesRequested": "Cambios pedidos",
    "review.rejected": "Rechazada",
    "review.expired": "Este enlace de revisión ha caducado.",
  },
  en: {
    "language.es": "ES",
    "language.en": "EN",
    "language.change": "Change language",
    "common.loading": "Loading…",
    "common.retry": "Retry",
    "common.close": "Close",
    "production.title": "Production",
    "production.projects": "Projects",
    "production.project": "Project",
    "production.openShots": "Open shots",
    "production.search": "Search shot, scene or artist",
    "production.refresh": "Refresh",
    "production.select": "Select",
    "production.assignee": "Assignee",
    "production.assignSelected": "Assign selection ({count})",
    "production.bulkResult": "Assigned: {ok} · conflicts/failures: {failed}",
    "production.open": "Open",
    "production.empty": "No shots match these filters.",
    "production.metric.total": "Shots",
    "production.metric.unassigned": "Unassigned",
    "production.metric.review": "In review",
    "production.metric.changes": "Changes",
    "production.metric.approved": "Approved",
    "production.metric.readyTakes": "With ready takes",
    "production.filter.all": "All",
    "production.filter.mine": "My shots",
    "production.filter.unassigned": "Unassigned",
    "production.filter.in_progress": "In progress",
    "production.filter.in_review": "In review",
    "production.filter.changes_requested": "Changes",
    "production.filter.approved": "Approved",
    "production.column.shot": "Shot",
    "production.column.assignee": "Assignee",
    "production.column.state": "State",
    "production.column.latest": "Latest",
    "production.column.approved": "Approved",
    "production.column.signatures": "Signatures",
    "production.column.takes": "Ready takes",
    "production.column.activity": "Activity",
    "production.column.actions": "Actions",
    "production.state.unassigned": "Unassigned",
    "production.state.todo": "To do",
    "production.state.inProgress": "In progress",
    "production.state.ready": "Ready for review",
    "production.state.inReview": "In review",
    "production.state.changes": "Changes requested",
    "production.state.approved": "Approved",
    "production.state.rejected": "Rejected",
    "production.state.skipped": "Skipped",
    "workflow.title": "Shot production",
    "workflow.assignee": "Assignee",
    "workflow.unassigned": "Unassigned",
    "workflow.priority": "Priority",
    "workflow.latest": "Latest version",
    "workflow.approved": "Approved version",
    "workflow.noApproved": "None",
    "workflow.assign": "Assign",
    "workflow.sending": "Sending…",
    "workflow.send": "Send for approval",
    "workflow.sent": "Submitted",
    "workflow.changes": "Changes requested",
    "workflow.versionApproved": "Approved",
    "workflow.draft": "Draft",
    "workflow.policyMissing": "Configure the project approval policy first.",
    "workflow.signatures": "Signatures",
    "workflow.compatibility": "Compatibility mode: staged approvals are not active yet.",
    "workflow.settings": "Team & approvals",
    "workflow.team": "Team",
    "workflow.roles": "Roles",
    "workflow.policy": "Approval chain",
    "workflow.policy.internal": "VFX + Director",
    "workflow.policy.director": "Director only",
    "workflow.policy.client": "VFX + Director + Client",
    "workflow.save": "Save",
    "workflow.saveChanges": "Save changes",
    "workflow.unsaved": "unsaved",
    "workflow.saved": "Saved",
    "workflow.discardConfirm": "Some role changes are not saved. Close and lose them?",
    "workflow.supervisorDefault": "Users with the global Supervisor role join the project as VFX Supervisor. You can remove it here.",
    "workflow.invite": "Invite external reviewer",
    "workflow.name": "Name",
    "workflow.email": "Email",
    "workflow.role": "Role",
    "workflow.stage": "Stage",
    "workflow.createLink": "Create named link",
    "workflow.copyLink": "Copy link",
    "workflow.linkCreated": "Link created. The token is shown only once.",
    "workflow.unregisteredTakes": "Completed takes without a version",
    "workflow.registerTake": "Create version",
    "workflow.archiving": "Archiving result…",
    "workflow.noVersions": "This shot has no versions yet.",
    "review.title": "Shot review",
    "review.as": "Reviewing as {name}",
    "review.empty": "No versions have been submitted for review.",
    "review.stage": "Stage",
    "review.note": "Note for the artist",
    "review.approve": "Approve",
    "review.requestChanges": "Request changes",
    "review.reject": "Reject",
    "review.pending": "Pending",
    "review.inReview": "In review",
    "review.approved": "Approved",
    "review.changesRequested": "Changes requested",
    "review.rejected": "Rejected",
    "review.expired": "This review link has expired.",
  },
};

function initialLocale() {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (SUPPORTED_LOCALES.includes(stored)) return stored;
  } catch (_e) { /* private mode */ }
  const browser = typeof navigator !== "undefined" ? navigator.language?.slice(0, 2) : "es";
  return normalizeLocale(browser);
}

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [locale, setLocaleState] = useState(initialLocale);
  useEffect(() => {
    if (typeof document !== "undefined") document.documentElement.lang = locale;
  }, [locale]);
  const setLocale = useCallback((next) => {
    const clean = normalizeLocale(next);
    setLocaleState(clean);
    try { window.localStorage.setItem(STORAGE_KEY, clean); } catch (_e) { /* private mode */ }
    if (typeof document !== "undefined") document.documentElement.lang = clean;
  }, []);
  const t = useCallback((key, params) => {
    return translate(messages, locale, key, params);
  }, [locale]);
  const formatDate = useCallback((value, options = {}) => {
    if (!value) return "—";
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return new Intl.DateTimeFormat(locale === "es" ? "es-ES" : "en-GB", options).format(date);
  }, [locale]);
  const value = useMemo(() => ({ locale, setLocale, t, formatDate }),
    [locale, setLocale, t, formatDate]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const value = useContext(I18nContext);
  if (!value) throw new Error("useI18n must be used inside I18nProvider");
  return value;
}

export { messages };
